/**
 * ═══════════════════════════════════════════════════════════════════════════
 * G-64 — ops onto a canvas, and a canvas into printer bytes
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * THE PREVIEW AND THE PRINT COME THROUGH HERE, TOGETHER. `renderReceipt` builds
 * one canvas; the builder shows that canvas, and `toCatPrinterJob` turns THAT
 * SAME bitmap into the bytes the TinyPrint receives. There is no second path,
 * so "what you see" and "what prints" cannot drift — which was the entire point
 * of the owner asking for a preview that is the actual print.
 *
 * Everything thoughtful lives in `receiptLayout.ts`, which is pure and tested,
 * and the wire format in `catPrinter.ts`. This file is deliberately dumb:
 * measure, draw, threshold.
 */

import { layoutReceipt, type ReceiptData } from "./receiptLayout";
import { RECEIPT_WIDTH_DOTS, type ReceiptTemplate } from "./receiptTemplate";

/**
 * A serif face would fall apart at 1 bit and 18 dots. A plain sans with real
 * weights survives the threshold, which is the only test that matters here.
 */
const FONT_STACK = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

function font(px: number, bold: boolean): string {
  return `${bold ? "700" : "400"} ${px}px ${FONT_STACK}`;
}

/**
 * Draws the ticket and returns the canvas, sized to the paper.
 *
 * `logo` is an already-loaded image or null. Loading it is the caller's job —
 * this stays synchronous so the preview can redraw on every keystroke in the
 * builder without a flicker of async.
 */
export function renderReceipt(
  template: ReceiptTemplate,
  data: ReceiptData,
  logo: HTMLImageElement | null,
): HTMLCanvasElement {
  const probe = document.createElement("canvas").getContext("2d")!;
  const measure = (text: string, px: number, bold: boolean) => {
    probe.font = font(px, bold);
    return probe.measureText(text).width;
  };

  const aspect =
    logo && logo.naturalWidth > 0 ? logo.naturalHeight / logo.naturalWidth : null;
  const { ops, height } = layoutReceipt(template, data, measure, aspect);

  const canvas = document.createElement("canvas");
  canvas.width = RECEIPT_WIDTH_DOTS;
  canvas.height = Math.max(1, Math.ceil(height));
  const ctx = canvas.getContext("2d")!;

  // Thermal paper is white and unprinted; everything drawn is burnt black.
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#000";
  ctx.strokeStyle = "#000";
  ctx.textBaseline = "alphabetic";

  for (const op of ops) {
    if (op.kind === "text") {
      ctx.font = font(op.px, op.bold);
      ctx.textAlign = op.align;
      ctx.fillText(op.text, op.x, op.y);
    } else if (op.kind === "rule") {
      ctx.fillRect(8, op.y, RECEIPT_WIDTH_DOTS - 16, 1);
    } else if (logo) {
      ctx.drawImage(logo, op.x, op.y, op.w, op.h);
    }
  }
  return canvas;
}

/**
 * Canvas → 1-bit rows, the form both the preview's "this is what burns" view
 * and the printer need.
 *
 * A thermal head has NO GREY: a dot is burnt or it is not. Everything darker
 * than mid-grey becomes black. That is why the builder shows this and not the
 * colour canvas — a logo that looks fine in colour can turn to mud here, and
 * the owner should see that before a customer does.
 */
export function toMonoBitmap(canvas: HTMLCanvasElement): {
  width: number;
  height: number;
  /** One byte per pixel: 1 = burn, 0 = leave white. */
  pixels: Uint8Array;
} {
  const ctx = canvas.getContext("2d")!;
  const { data, width, height } = ctx.getImageData(
    0,
    0,
    canvas.width,
    canvas.height,
  );
  const pixels = new Uint8Array(width * height);
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    const alpha = data[i + 3];
    // Anything transparent is paper, not black — a PNG logo with a cut-out
    // background must not print as a solid block.
    if (alpha < 128) {
      pixels[p] = 0;
      continue;
    }
    // Rec. 601 luma: closer to how ink reads than a flat average.
    const luma = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    pixels[p] = luma < 128 ? 1 : 0;
  }
  return { width, height, pixels };
}

/**
 * A 1-bit bitmap as a canvas you can put on screen.
 *
 * Extracted for G-65: the note preview needs exactly this, and a second copy
 * of a pixel loop is how two previews start disagreeing about what "black"
 * means. Burnt dots are black, everything else is paper — no greys, because
 * the head has none.
 */
export function drawMonoBitmap(bitmap: {
  width: number;
  height: number;
  pixels: Uint8Array;
}): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, bitmap.width);
  canvas.height = Math.max(1, bitmap.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  const image = ctx.createImageData(canvas.width, canvas.height);
  for (let i = 0; i < bitmap.pixels.length; i += 1) {
    const v = bitmap.pixels[i] ? 0 : 255;
    image.data[i * 4] = v;
    image.data[i * 4 + 1] = v;
    image.data[i * 4 + 2] = v;
    image.data[i * 4 + 3] = 255;
  }
  ctx.putImageData(image, 0, 0);
  return canvas;
}
